import { useMemo } from 'react';

import { AppIcon, ColorToken } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGetTranscriptContentQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { TranscriptCue } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

const {
  modules: { coachingDashboard },
} = appStrings;

const SPEAKER_COLORS = [
  'Brand/500',
  'Success/600',
  'Warning/500',
  'Error/500',
  'Brand/700',
  'Success/400',
] as const;

type TranscriptViewProps = Readonly<{
  recordingId: number;
  meetingDate: string;
  onBack: () => void;
}>;

export const TranscriptView = ({ recordingId, meetingDate, onBack }: TranscriptViewProps) => {
  const {
    data: cues = [],
    isLoading: loading,
    isError: error,
  } = useGetTranscriptContentQuery(recordingId);

  const speakerColors = useMemo(() => {
    const map = new Map<string, number>();
    let index = 0;

    for (const cue of cues) {
      if (!map.has(cue.speaker)) {
        map.set(cue.speaker, index++);
      }
    }

    return map;
  }, [cues]);

  return (
    <Box sx={containerSx}>
      <Box sx={headerSx}>
        <Box sx={backRowSx} onClick={onBack} role="button" tabIndex={0}>
          <AppIcon iconName="ArrowLeft" diameter="16px" color="Brand/500" />
          <Typography variant="text-sm-semibold" sx={backTextSx}>
            {coachingDashboard.backToAllSessions}
          </Typography>
        </Box>
        <Box>
          <Typography variant="text-lg-semibold">{coachingDashboard.transcriptTitle}</Typography>
          <Typography variant="text-sm-regular" sx={subtitleSx}>
            {meetingDate}
          </Typography>
        </Box>
      </Box>
      <Box sx={contentSx}>
        {loading && (
          <Typography variant="text-sm-regular" sx={statusTextSx}>
            {coachingDashboard.transcriptLoading}
          </Typography>
        )}
        {error && (
          <Typography variant="text-sm-regular" sx={errorTextSx}>
            {coachingDashboard.transcriptError}
          </Typography>
        )}
        {!loading &&
          !error &&
          cues.map((cue) => (
            <TranscriptBubble
              key={cue.startTime}
              cue={cue}
              colorIndex={speakerColors.get(cue.speaker) ?? 0}
            />
          ))}
      </Box>
    </Box>
  );
};

const formatTimestamp = (time: string): string => {
  const parts = time.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  if (hours > 0)
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

type TranscriptBubbleProps = Readonly<{
  cue: TranscriptCue;
  colorIndex: number;
}>;

const TranscriptBubble = ({ cue, colorIndex }: TranscriptBubbleProps) => {
  const color = SPEAKER_COLORS[colorIndex % SPEAKER_COLORS.length];

  return (
    <Box sx={bubbleWrapperSx}>
      <Box sx={bubbleHeaderSx}>
        <Typography variant="text-xs-semibold" sx={speakerNameSx(color)}>
          {cue.speaker}
        </Typography>
        <Typography variant="text-xs-regular" sx={timestampSx}>
          {formatTimestamp(cue.startTime)}
        </Typography>
      </Box>
      <Box sx={bubbleSx}>
        <Typography variant="text-sm-regular" sx={bubbleTextSx}>
          {cue.text}
        </Typography>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  padding: backrs.spacing['spacing-lg'],
  paddingBottom: backrs.spacing['spacing-md'],
  position: 'sticky',
  top: 0,
  backgroundColor: backrs.colors['Colors/Base/White'],
  zIndex: 1,
  borderBottom: `1px solid ${backrs.colors['Gray/200']}`,
});

const backRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const backTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-lg'],
  overflow: 'auto',
  flex: 1,
});

const statusTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  textAlign: 'center',
  padding: backrs.spacing['spacing-3xl'],
});

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Error/600'],
  textAlign: 'center',
  padding: backrs.spacing['spacing-3xl'],
});

const bubbleWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const bubbleHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const speakerNameSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors[color],
  });

const timestampSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const bubbleSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Gray/950']}`,
  borderRadius: backrs.radius['radius-md'],
  padding: backrs.spacing['spacing-lg'],
});

const bubbleTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
